﻿import {useEffect, useRef} from "react";
import {getQueryParamsFromPlace, parseLatLngParams} from "../../util/AddressUtils";
import {useLocationSearchParams} from "./useLocationSearchParams";
import {useLocation} from "react-router";
import GeocoderResult = google.maps.GeocoderResult;

interface RedirectSearchByLocationParams {
    defaultLocation: GeocoderResult | undefined
    failedToGetByBrowserLatLng: boolean
    failedToGetByIp: boolean
    failedToGetByLocalStorage: boolean
    geocodedPlaceByBrowserLatLng: GeocoderResult | undefined
    geocodedPlaceByIpLatLng: GeocoderResult | undefined
    geocodedPlaceByLocalStorageLatLng: GeocoderResult | undefined
    geocodingAddressFromParamsFailed: boolean
}

export const useRedirectSearchByLocationParams = ({
                                                        defaultLocation,
                                                        failedToGetByBrowserLatLng,
                                                        failedToGetByIp,
                                                        failedToGetByLocalStorage,
                                                        geocodedPlaceByBrowserLatLng,
                                                        geocodedPlaceByIpLatLng,
                                                        geocodedPlaceByLocalStorageLatLng,
                                                        geocodingAddressFromParamsFailed,
                                                    }: RedirectSearchByLocationParams) => {
    const [currentLocationParams, setCurrentLocationParams] = useLocationSearchParams();
    // Read, not a dependency: re-running on every address change would let a failure
    // flag that stays set undo later searches.
    const currentLocationParamsRef = useRef(currentLocationParams);
    currentLocationParamsRef.current = currentLocationParams;
    const location = useLocation();

    useEffect(() => {
        // Only on bare /yards (/search redirects there). A /yards city path never falls
        // back to the visitor's area: the path names the place. The found area replaces the
        // address, so Back doesn't return to the empty page (securspace-ui#3053).
        if (location.pathname !== '/yards') return;
        // Coordinates are a place already (e.g. after a map drag): never replace them.
        const params = currentLocationParamsRef.current;
        if (parseLatLngParams(params) && !(params?.city && params?.state)) return;
        if (geocodingAddressFromParamsFailed && geocodedPlaceByLocalStorageLatLng && !failedToGetByLocalStorage) {
            // URL address not valid but local storage works, redirect
            setCurrentLocationParams({
                ...getQueryParamsFromPlace(geocodedPlaceByLocalStorageLatLng, 'short_name', false)
            }, {replace: true});
        } else if (failedToGetByLocalStorage && geocodedPlaceByBrowserLatLng && !failedToGetByBrowserLatLng) {
            // can't get from local storage, redirect to browser lat/lng
            setCurrentLocationParams({
                ...getQueryParamsFromPlace(geocodedPlaceByBrowserLatLng, 'short_name', false)
            }, {replace: true});
        } else if(failedToGetByBrowserLatLng && geocodedPlaceByIpLatLng && !failedToGetByIp) {
            // can't get by browser lat/lng, redirect by ip
            setCurrentLocationParams({
                ...getQueryParamsFromPlace(geocodedPlaceByIpLatLng, 'short_name', false)
            }, {replace: true});
        } else if (failedToGetByIp && defaultLocation) {
            // can't get by ip, redirect to default location
            setCurrentLocationParams({
                ...getQueryParamsFromPlace(defaultLocation, 'short_name', false)
            }, {replace: true});
        }
    }, [defaultLocation, failedToGetByBrowserLatLng, failedToGetByIp, failedToGetByLocalStorage, geocodedPlaceByBrowserLatLng, geocodedPlaceByIpLatLng, geocodedPlaceByLocalStorageLatLng, geocodingAddressFromParamsFailed, setCurrentLocationParams, location.pathname]);
}
